import { IFilterSection } from "@faire/web-api/indigofair/data/IFilterSection";
import React, { useMemo } from "react";

import { useFilterStore } from "@faire/retailer/components/Filters/useFilterStore";
import { InsiderExpandedCoverageBanner } from "@faire/retailer/features/SlimBanners/InsiderExpandedCoverageBanner";
import { useCategoryPageRequestId } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useCategoryPageRequestId";
import { useIsCategoryPageLoadedAndHasNoResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useIsCategoryPageLoadedAndHasNoResults";
import { useCategoryPageHeaderAndSubheaderTexts } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/HeaderAndSubheader/useCategoryPageHeaderAndSubheaderTexts";
import { BrandFirstResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/__internal__/BrandFirst/CategoryPageBrandFirstResults";
import { CategoryPageNoResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/__internal__/NoResults/CategoryPageNoResults";
import { ProductFirstResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/__internal__/ProductFirst/CategoryPageProductFirstResults";
import { useIsProductOrBrandGridLoading } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/__internal__/useIsProductOrBrandGridLoading";
import { useEnableFullWidthCatnavXP } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useEnableFullWidthCatnavXP";
import { useShowProductFirstResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/useShowProductFirstResults/useShowProductFirstResults";
import { useCurrentCategoryModel } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useCurrentCategoryModel";

/** Renders a list of products or brands based on the category page view state */
export const CategoryPageProductOrBrandGrid = ({
  categoryPageLoadTimingRef,
  isScrollingToTop,
  onFirstBatchVisible,
}: {
  categoryPageLoadTimingRef?: React.Ref<HTMLDivElement>;
  isScrollingToTop?: boolean;
  onFirstBatchVisible?: () => void;
}) => {
  const { headerText } = useCategoryPageHeaderAndSubheaderTexts();
  const isLoading = useIsProductOrBrandGridLoading();

  const categoryModel = useCurrentCategoryModel();
  const requestId = useCategoryPageRequestId();
  const showEmptyList = useIsCategoryPageLoadedAndHasNoResults();
  const showProductFirstResults = useShowProductFirstResults();
  const isFullWidthXPEnabled = useEnableFullWidthCatnavXP();

  const {
    data: { filterSections },
  } = useFilterStore(["filterSections"]);

  const shouldDisplayInsiderBanner = useMemo(() => {
    return !!filterSections.find(
      (filter) => filter.field_name === IFilterSection.FieldName.INSIDER
    );
  }, [filterSections]);

  if (showEmptyList) {
    return (
      <CategoryPageNoResults
        showProductFirstResults={showProductFirstResults}
        categoryPageLoadTimingRef={categoryPageLoadTimingRef}
      />
    );
  }

  if (showProductFirstResults) {
    return (
      <>
        {shouldDisplayInsiderBanner ? <InsiderExpandedCoverageBanner /> : null}
        <ProductFirstResults
          isLoading={isLoading}
          requestId={requestId}
          headerText={headerText}
          categoryModel={categoryModel}
          categoryPageLoadTimingRef={categoryPageLoadTimingRef}
          isScrollingToTop={isScrollingToTop}
          onFirstBatchVisible={onFirstBatchVisible}
          shouldRenderExtraTileOnXLScreen={isFullWidthXPEnabled}
        />
      </>
    );
  }

  return (
    <>
      {shouldDisplayInsiderBanner ? <InsiderExpandedCoverageBanner /> : null}
      <BrandFirstResults
        isLoading={isLoading}
        requestId={requestId}
        headerText={headerText}
        categoryModel={categoryModel}
        categoryPageLoadTimingRef={categoryPageLoadTimingRef}
        isScrollingToTop={isScrollingToTop}
        onFirstBatchVisible={onFirstBatchVisible}
        shouldRenderExtraTileOnXLScreen={isFullWidthXPEnabled}
      />
    </>
  );
};
